import { Intercom, update } from '@intercom/messenger-js-sdk';
import { AuthApi } from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { SHELL_TAB_BAR_CHROME_CHANGE_EVENT } from '@/core/utils/shell-chrome';

const INTERCOM_APP_ID = 'geacv4jt';
const INTERCOM_READY_POLL_MS = 100;
const INTERCOM_READY_TIMEOUT_MS = 8000;
/** Gap between Cap floating tab bar clearance and the Intercom bubble. */
const INTERCOM_LAUNCHER_GAP_ABOVE_TAB_PX = 12;
/** Typical Intercom launcher diameter — used to stack notifications above it. */
const INTERCOM_LAUNCHER_SIZE_PX = 56;

const MESSENGER_FRAME_SELECTOR = [
  '.intercom-messenger-frame',
  'iframe[name="intercom-messenger-frame"]',
  'iframe#intercom-frame',
].join(', ');

const LAUNCHER_FRAME_SELECTOR = [
  '.intercom-lightweight-app-launcher',
  '.intercom-namespace .intercom-launcher',
  'iframe[name="intercom-launcher-frame"]',
  '.intercom-launcher-frame',
].join(', ');

const NOTIFICATIONS_FRAME_SELECTOR = [
  'iframe[name="intercom-notifications-frame"]',
  '.intercom-notifications-frame',
].join(', ');

// Module-level state so one boot is shared across all consumers (bootstrap + useIntercom)
let isBootedRef = false;
let messengerSafeAreaObserver: MutationObserver | null = null;
let messengerLifecycleBound = false;

export interface IntercomIdentity {
  user_id: string;
  name?: string;
  email?: string;
  user_hash?: string;
  /** When known (e.g. current workspace), sent to Intercom as company for support context */
  workspace_id?: string;
  workspace_name?: string;
}

type WorkspaceContext = {
  id: string;
  name?: string | null;
};

type IntercomFn = (method: string, value?: string | unknown) => void;

type IntercomQueueStub = IntercomFn & { q?: unknown[] };

type IntercomBootSettings = {
  app_id: string;
  user_id: string;
  name?: string;
  email?: string;
  user_hash?: string;
  hide_default_launcher?: boolean;
  alignment?: 'left' | 'right';
  vertical_padding?: number;
  horizontal_padding?: number;
  companies?: Array<{ company_id: string; name: string }>;
};

export type SafeAreaInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

/** Live env(safe-area-inset-*) from a fixed probe (works with viewport-fit=cover). */
export function readSafeAreaInsets(): SafeAreaInsets {
  if (typeof document === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  document.documentElement.appendChild(probe);
  const style = getComputedStyle(probe);
  const insets = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

/**
 * Concrete px vars for Intercom CSS (messenger frame + launcher).
 * Prefer these over raw env() — Intercom injects late and some WebViews
 * resolve env inconsistently on dynamically inserted nodes.
 */
export function syncIntercomSafeAreaCssVars(
  insets: SafeAreaInsets = readSafeAreaInsets(),
): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--intercom-safe-top', `${insets.top}px`);
  root.style.setProperty('--intercom-safe-right', `${insets.right}px`);
  root.style.setProperty('--intercom-safe-bottom', `${insets.bottom}px`);
  root.style.setProperty('--intercom-safe-left', `${insets.left}px`);
}

/** Resolve `--shell-mobile-tab-bar-clearance` (includes home-indicator pad). */
function readShellTabBarClearancePx(): number {
  if (typeof document === 'undefined') return 0;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;height:var(--shell-mobile-tab-bar-clearance,0px)';
  document.documentElement.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  return Number.isFinite(px) && px > 0 ? px : 0;
}

/**
 * Bottom offset for the Intercom launcher from the viewport edge.
 * Cap: above floating tab bar (+ gap). Browser: safe-area + small base only.
 */
export function resolveIntercomLauncherBottomPx(
  insets: SafeAreaInsets = readSafeAreaInsets(),
): number {
  if (!isCapacitorNative()) {
    return Math.round(Math.max(20, insets.bottom + 12));
  }
  const tabClearance = readShellTabBarClearancePx();
  // Fallback if the token failed to resolve (should match tokens.css ~5.5rem).
  const clearance =
    tabClearance > 0 ? tabClearance : 88 + Math.max(20, insets.bottom);
  return Math.round(clearance + INTERCOM_LAUNCHER_GAP_ABOVE_TAB_PX);
}

/**
 * Pin Cap launcher / notification bubbles above the floating tab bar.
 * CSS alone often loses to Intercom inline `bottom` — reinforce with !important.
 */
export function applyIntercomLauncherClearance(
  insets: SafeAreaInsets = readSafeAreaInsets(),
): void {
  if (typeof document === 'undefined' || !isCapacitorNative()) return;

  const bottom = resolveIntercomLauncherBottomPx(insets);
  const right = Math.round(Math.max(16, insets.right + 12));
  const notificationsBottom = bottom + INTERCOM_LAUNCHER_SIZE_PX + 8;
  const root = document.documentElement;
  root.style.setProperty('--intercom-launcher-bottom', `${bottom}px`);
  root.style.setProperty('--intercom-launcher-right', `${right}px`);
  root.style.setProperty(
    '--intercom-notifications-bottom',
    `${notificationsBottom}px`,
  );

  document.querySelectorAll(LAUNCHER_FRAME_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.setProperty('bottom', `${bottom}px`, 'important');
    node.style.setProperty('right', `${right}px`, 'important');
  });

  document.querySelectorAll(NOTIFICATIONS_FRAME_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.setProperty('bottom', `${notificationsBottom}px`, 'important');
    node.style.setProperty('right', `${right}px`, 'important');
  });
}

/**
 * Pin the open messenger iframe to Cap safe areas.
 * `vertical_padding` only moves the launcher — the full-screen messenger
 * uses fixed inset:0 under StatusBar.overlaysWebView and sits under the notch
 * unless we override the outer frame (iframe content cannot see env()).
 *
 * Iframes are replaced elements: top/right/bottom/left + width/height:auto
 * collapses to a tiny box. Use explicit width/height calc instead.
 */
export function applyIntercomMessengerSafeArea(
  insets: SafeAreaInsets = readSafeAreaInsets(),
): void {
  if (typeof document === 'undefined' || !isCapacitorNative()) return;
  syncIntercomSafeAreaCssVars(insets);

  const top = `${insets.top}px`;
  const right = `${insets.right}px`;
  const bottom = `${insets.bottom}px`;
  const left = `${insets.left}px`;
  const width = `calc(100vw - ${insets.left}px - ${insets.right}px)`;
  const height = `calc(100dvh - ${insets.top}px - ${insets.bottom}px)`;

  const nodes = document.querySelectorAll(MESSENGER_FRAME_SELECTOR);
  nodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.setProperty('position', 'fixed', 'important');
    node.style.setProperty('top', top, 'important');
    node.style.setProperty('left', left, 'important');
    node.style.setProperty('right', 'auto', 'important');
    node.style.setProperty('bottom', 'auto', 'important');
    node.style.setProperty('width', width, 'important');
    node.style.setProperty('height', height, 'important');
    node.style.setProperty('max-width', 'none', 'important');
    node.style.setProperty('max-height', 'none', 'important');
    node.style.setProperty('box-sizing', 'border-box', 'important');
  });
}

function ensureMessengerSafeAreaObserver(): void {
  if (typeof document === 'undefined' || !isCapacitorNative()) return;
  if (messengerSafeAreaObserver) return;

  let scheduled = false;
  const scheduleApply = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      applyIntercomMessengerSafeArea();
      applyIntercomLauncherClearance();
    });
  };

  messengerSafeAreaObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (
          node.matches(MESSENGER_FRAME_SELECTOR) ||
          node.querySelector(MESSENGER_FRAME_SELECTOR) ||
          node.matches(LAUNCHER_FRAME_SELECTOR) ||
          node.querySelector(LAUNCHER_FRAME_SELECTOR) ||
          node.matches(NOTIFICATIONS_FRAME_SELECTOR) ||
          node.querySelector(NOTIFICATIONS_FRAME_SELECTOR)
        ) {
          scheduleApply();
          return;
        }
      }
    }
  });
  messengerSafeAreaObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function bindMessengerSafeAreaLifecycle(): void {
  if (messengerLifecycleBound || typeof window === 'undefined') return;
  if (!isCapacitorNative()) return;
  messengerLifecycleBound = true;

  ensureMessengerSafeAreaObserver();
  applyIntercomMessengerSafeArea();
  applyIntercomLauncherClearance();

  document.documentElement.addEventListener(
    SHELL_TAB_BAR_CHROME_CHANGE_EVENT,
    () => {
      applyIntercomLauncherClearance();
    },
  );

  try {
    const intercomFn = getIntercomFn();
    intercomFn?.('onShow', () => {
      // Frame mounts after the show callback on some SDK builds.
      applyIntercomMessengerSafeArea();
      window.requestAnimationFrame(() => applyIntercomMessengerSafeArea());
      window.setTimeout(() => applyIntercomMessengerSafeArea(), 50);
      window.setTimeout(() => applyIntercomMessengerSafeArea(), 250);
    });
    intercomFn?.('onHide', () => {
      applyIntercomLauncherClearance();
    });
  } catch {
    // no-op
  }
}

/**
 * Launcher padding that clears the home indicator (and Cap tab bar on native).
 * CSS + applyIntercomLauncherClearance mirror this for widgets that ignore
 * vertical_padding on mobile WebViews.
 */
export function resolveIntercomLauncherPadding(): {
  vertical_padding: number;
  horizontal_padding: number;
} {
  const insets = readSafeAreaInsets();
  return {
    vertical_padding: resolveIntercomLauncherBottomPx(insets),
    horizontal_padding: Math.round(Math.max(16, insets.right + 12)),
  };
}

function syncIntercomChromeInsets(): void {
  const insets = readSafeAreaInsets();
  syncIntercomSafeAreaCssVars(insets);
  applyIntercomMessengerSafeArea(insets);
  applyIntercomLauncherClearance(insets);
  if (!isBootedRef) return;
  try {
    const pad = resolveIntercomLauncherPadding();
    update({
      app_id: INTERCOM_APP_ID,
      ...pad,
    } as Parameters<typeof update>[0]);
  } catch {
    // no-op
  }
}

function getIntercomFn(): IntercomFn | null {
  if (typeof window === 'undefined') return null;
  const win = window as unknown as { Intercom?: IntercomFn };
  return win.Intercom ?? (Intercom as unknown as IntercomFn);
}

/** True once the widget script replaced the npm loader queue stub. */
function isIntercomApiReady(): boolean {
  if (typeof window === 'undefined') return false;
  const intercomFn = (window as unknown as { Intercom?: IntercomQueueStub })
    .Intercom;
  if (typeof intercomFn !== 'function') return false;
  return !Array.isArray(intercomFn.q);
}

function waitForIntercomApiReady(
  timeoutMs = INTERCOM_READY_TIMEOUT_MS,
): Promise<boolean> {
  if (isIntercomApiReady()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (isIntercomApiReady()) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, INTERCOM_READY_POLL_MS);
    };
    tick();
  });
}

function invokeIntercom(method: string, value?: string): void {
  try {
    const intercomFn = getIntercomFn();
    if (!intercomFn) return;
    if (value?.trim()) {
      intercomFn(method, value.trim());
    } else {
      intercomFn(method);
    }
  } catch {
    // no-op
  }
}

function invokeShowNewMessage(prefilledText: string): void {
  const trimmed = prefilledText.trim();
  invokeIntercom('showNewMessage', trimmed || undefined);
}

/**
 * Boot Intercom with the given identity. Safe to call multiple times; subsequent
 * calls update identity. Called on app start when user is logged in (see useIntercomBootstrap).
 * When workspace_id and workspace_name are present, they are sent to Intercom as company data.
 */
export function bootIntercom(identity: IntercomIdentity): void {
  syncIntercomSafeAreaCssVars();
  applyIntercomLauncherClearance();
  const launcherPad = resolveIntercomLauncherPadding();
  const settings: IntercomBootSettings = {
    app_id: INTERCOM_APP_ID,
    user_id: identity.user_id,
    // Web opens the messenger from Hilfe & Support only; an explicit `false`
    // made the bubble appear after the first close and cover controls
    // (DEV-535). Cap keeps it above the tab bar (DEV-472).
    hide_default_launcher: !isCapacitorNative(),
    alignment: 'right',
    vertical_padding: launcherPad.vertical_padding,
    horizontal_padding: launcherPad.horizontal_padding,
    ...(identity.name?.trim() && { name: identity.name.trim() }),
    ...(identity.email?.trim() && { email: identity.email.trim() }),
    ...(identity.user_hash && { user_hash: identity.user_hash }),
    ...(identity.workspace_id?.trim() &&
      identity.workspace_name?.trim() && {
        companies: [
          {
            company_id: identity.workspace_id.trim(),
            name: identity.workspace_name.trim(),
          },
        ],
      }),
  };
  try {
    const payload = settings as Parameters<typeof Intercom>[0];
    if (!isBootedRef) {
      Intercom(payload);
      isBootedRef = true;
    } else {
      update(payload);
    }
    void waitForIntercomApiReady().then((ready) => {
      if (ready) bindMessengerSafeAreaLifecycle();
    });
  } catch {
    // no-op
  }
}

export function isIntercomBooted(): boolean {
  return isBootedRef;
}

async function ensureIntercomBooted(
  workspace?: WorkspaceContext | null,
): Promise<boolean> {
  if (isBootedRef) return true;
  try {
    const res = await AuthApi.getIntercomIdentity();
    const data = res?.data ?? res;
    const identity = data as IntercomIdentity;
    if (!identity?.user_id) return false;
    bootIntercom({
      ...identity,
      ...(workspace?.id && {
        workspace_id: workspace.id,
        workspace_name: workspace.name ?? workspace.id,
      }),
    });
    return isBootedRef;
  } catch {
    return false;
  }
}

/**
 * Boot if needed, wait for the widget, then open a new message with optional prefill.
 * Retries prefill once after the messenger finishes loading (Intercom quirk on cold boot).
 */
export async function openIntercomNewMessage(
  prefilledText: string,
  workspace?: WorkspaceContext | null,
): Promise<boolean> {
  const trimmed = prefilledText.trim();
  const bootOk = await ensureIntercomBooted(workspace);
  if (!bootOk) return false;

  const wasApiReady = isIntercomApiReady();
  await waitForIntercomApiReady(wasApiReady ? 500 : INTERCOM_READY_TIMEOUT_MS);
  bindMessengerSafeAreaLifecycle();

  invokeShowNewMessage(trimmed);
  applyIntercomMessengerSafeArea();

  if (trimmed) {
    const retryDelay = wasApiReady ? 400 : 900;
    window.setTimeout(() => invokeShowNewMessage(trimmed), retryDelay);
  }

  return true;
}

export function useIntercom() {
  const [bootError, setBootError] = useState<Error | null>(null);
  const currentWorkspace = useCurrentWorkspace();

  const boot = useCallback((identity: IntercomIdentity) => {
    setBootError(null);
    try {
      bootIntercom(identity);
    } catch (err) {
      setBootError(err instanceof Error ? err : new Error(String(err)));
    }
  }, []);

  const show = useCallback(() => {
    bindMessengerSafeAreaLifecycle();
    invokeIntercom('show');
    applyIntercomMessengerSafeArea();
  }, []);

  const showNewMessage = useCallback(
    (prefilledText: string) => {
      if (!isBootedRef) return;
      void openIntercomNewMessage(prefilledText, currentWorkspace);
    },
    [currentWorkspace],
  );

  const isBooted = useCallback(() => isBootedRef, []);

  /** Boot on demand if needed, then open the messenger. Use this for Support/Chat entry points (sidebar, bottom nav). */
  const openSupport = useCallback(async () => {
    try {
      const bootOk = await ensureIntercomBooted(currentWorkspace);
      if (!bootOk) return;
      await waitForIntercomApiReady(
        isIntercomApiReady() ? 500 : INTERCOM_READY_TIMEOUT_MS,
      );
      show();
    } catch {
      // no-op
    }
  }, [currentWorkspace, show]);

  const openNewMessage = useCallback(
    async (prefilledText: string) =>
      openIntercomNewMessage(prefilledText, currentWorkspace),
    [currentWorkspace],
  );

  return {
    boot,
    show,
    showNewMessage,
    openSupport,
    openNewMessage,
    isBooted,
    bootError,
  };
}

/**
 * Call once in the app shell (e.g. next to useAuthSession). When the user is
 * logged in, fetches Intercom identity from the backend and boots Intercom.
 * Intercom is then available for the rest of the session (e.g. showNewMessage on ProductsSetupWizardPage).
 */
export function useIntercomBootstrap(): void {
  const user = useAuthStore((state) => state.user);
  const tokens = useAuthStore((state) => state.tokens);
  const hydrated = useAuthStore((state) => state.hydrated);
  const currentWorkspace = useCurrentWorkspace();
  const bootedRef = useRef(false);

  useEffect(() => {
    if (!hydrated || !tokens?.accessToken || bootedRef.current || isBootedRef) {
      return;
    }

    bootedRef.current = true;
    let cancelled = false;

    void (async () => {
      try {
        const res = await AuthApi.getIntercomIdentity();
        if (cancelled) return;
        const data = res?.data ?? res;
        const identity = data as IntercomIdentity;
        if (identity?.user_id) {
          bootIntercom({
            ...identity,
            ...(currentWorkspace?.id && {
              workspace_id: currentWorkspace.id,
              workspace_name: currentWorkspace.name ?? currentWorkspace.id,
            }),
          });
        }
      } catch {
        bootedRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hydrated,
    user,
    tokens?.accessToken,
    currentWorkspace?.id,
    currentWorkspace?.name,
  ]);

  // Re-apply launcher + messenger insets after rotate / safe-area changes (Cap notch).
  useEffect(() => {
    if (!hydrated || !tokens?.accessToken) return;
    syncIntercomChromeInsets();
    const sync = () => syncIntercomChromeInsets();
    window.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('resize', sync);
    };
  }, [hydrated, tokens?.accessToken]);
}
